import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  FaCalendarAlt, FaMagic, FaCheck, FaTimes, FaPlus, FaTrashAlt,
  FaExclamationTriangle, FaSpinner, FaArrowRight,
} from 'react-icons/fa';
import friendlyError from '../../utils/friendlyError';
import { actualizarAccion as actualizarAccionCrisis } from '../../services/crisisService';
import {
  TechShell, TechPageHeader, TechSection, TechInput, TechSelect, TechButton,
  TechButtonGhost, StatusPill, TechChip, Empty, Loading, ErrorBox,
  borderSoft, mono, panelDeep,
} from '../../styles/techComponents';
import {
  getAgenda, crearItems, actualizarItem, borrarItem, generarPlan,
} from '../../services/agendaService';

/* ============================================================
   Agenda — el día a día armado con los datos de la plataforma.
   Junta lo que uno carga con lo que ya vence en otras secciones,
   y la IA propone qué hacer cada día (se acepta o se descarta).
   ============================================================ */

const hoyISO = () => new Date().toISOString().slice(0, 10);
const sumarDias = (iso, n) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};
const nombreDia = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('es-AR', {
    weekday: 'long', day: 'numeric', month: 'long',
  });

const COLOR_PRIORIDAD = {
  alta: 'var(--alert-error)',
  media: 'var(--alert-warning)',
  baja: 'var(--text-disabled)',
};

const Dia = styled.section`
  margin-bottom: 1.1rem;

  .cabecera {
    display: flex;
    align-items: baseline;
    gap: 0.6rem;
    margin-bottom: 0.5rem;

    h3 {
      margin: 0;
      font-size: 0.95rem;
      color: var(--text-primary);
      text-transform: capitalize;
    }
    .tag {
      font-family: ${mono};
      font-size: 0.6rem;
      letter-spacing: 0.16em;
      text-transform: uppercase;
      color: var(--btn-primary);
    }
    .vacio { font-size: 0.82rem; color: var(--text-disabled); }
  }
`;

const Item = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;
  padding: 0.65rem 0.8rem;
  margin-bottom: 0.4rem;
  border: 1px solid ${borderSoft};
  border-left: 3px solid ${({ $prio }) => COLOR_PRIORIDAD[$prio] || 'var(--btn-primary)'};
  border-radius: 8px;
  background: ${panelDeep};
  opacity: ${({ $hecho }) => ($hecho ? 0.5 : 1)};

  .cuerpo { flex: 1; min-width: 0; }
  .titulo {
    font-size: 0.9rem;
    color: var(--text-primary);
    text-decoration: ${({ $hecho }) => ($hecho ? 'line-through' : 'none')};
  }
  .desc { font-size: 0.82rem; color: var(--text-secondary); margin-top: 3px; line-height: 1.45; }
  .motivo {
    font-family: ${mono};
    font-size: 0.64rem;
    letter-spacing: 0.06em;
    color: var(--text-disabled);
    margin-top: 5px;
  }
  .meta { display: flex; align-items: center; gap: 0.4rem; flex-wrap: wrap; margin-top: 5px; }
  .hora {
    font-family: ${mono};
    font-size: 0.72rem;
    color: var(--btn-primary);
    flex-shrink: 0;
    min-width: 42px;
    padding-top: 2px;
  }
  .acciones { display: flex; gap: 0.3rem; flex-shrink: 0; }
`;

const MiniBtn = styled.button`
  background: transparent;
  border: 1px solid ${borderSoft};
  border-radius: 6px;
  color: var(--text-secondary);
  width: 28px;
  height: 28px;
  cursor: pointer;
  font-size: 0.72rem;
  &:hover { color: ${({ $danger }) => ($danger ? 'var(--alert-error)' : 'var(--alert-success)')}; }
`;

const Pendientes = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 0.6rem;
`;

const Form = styled.div`
  display: grid;
  grid-template-columns: 130px 90px 1.6fr 130px auto;
  gap: 0.5rem;
  align-items: end;
  margin-bottom: 0.9rem;
  @media (max-width: 900px) { grid-template-columns: 1fr 1fr; }
`;

const Lbl = styled.label`
  font-family: ${mono};
  font-size: 0.62rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-secondary);
  display: block;
  margin-bottom: 0.2rem;
`;

const Propuesta = styled.div`
  border: 1px solid rgba(255, 209, 102, 0.4);
  border-left: 3px solid var(--alert-warning);
  border-radius: 8px;
  padding: 0.7rem 0.85rem;
  margin-bottom: 0.45rem;
  background: rgba(11, 19, 32, 0.6);

  .top {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-family: ${mono};
    font-size: 0.62rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--text-disabled);
  }
  .titulo { font-size: 0.9rem; color: var(--text-primary); margin-top: 4px; }
  .desc { font-size: 0.82rem; color: var(--text-secondary); margin-top: 3px; line-height: 1.45; }
  .motivo { font-size: 0.78rem; color: var(--btn-primary); margin-top: 5px; }
`;

const EMPTY = { fecha: hoyISO(), hora: '', titulo: '', tipo: 'tarea' };

export default function Agenda() {
  const [dias, setDias] = useState(7);
  const [data, setData] = useState({ items: [], derivados: [] });
  const [estado, setEstado] = useState({ loading: true, error: null });
  const [form, setForm] = useState(EMPTY);
  const [propuestas, setPropuestas] = useState(null);
  const [generando, setGenerando] = useState(false);
  const [msg, setMsg] = useState(null);

  const desde = hoyISO();
  const hasta = useMemo(() => sumarDias(desde, dias - 1), [desde, dias]);

  const cargar = useCallback(async () => {
    setEstado({ loading: true, error: null });
    try {
      setData(await getAgenda({ desde, hasta }));
      setEstado({ loading: false, error: null });
    } catch (e) {
      setEstado({ loading: false, error: friendlyError(e) });
    }
  }, [desde, hasta]);

  useEffect(() => { cargar(); }, [cargar]);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(null), 3000); };

  // Los derivados sin fecha son deuda acumulada (reclamos abiertos): no van a
  // un día, van a una lista aparte de "pendientes".
  const { porDia, sinFecha } = useMemo(() => {
    const mapa = {};
    const sueltos = [];
    for (const it of [...(data.items || []), ...(data.derivados || [])]) {
      if (!it.fecha) { sueltos.push(it); continue; }
      const f = String(it.fecha).slice(0, 10);
      (mapa[f] = mapa[f] || []).push(it);
    }
    for (const f of Object.keys(mapa)) {
      mapa[f].sort((a, b) => (a.hora || '99') < (b.hora || '99') ? -1 : 1);
    }
    return { porDia: mapa, sinFecha: sueltos };
  }, [data]);

  const fechas = useMemo(
    () => Array.from({ length: dias }, (_, i) => sumarDias(desde, i)),
    [desde, dias]
  );

  async function onGenerar() {
    setGenerando(true);
    setPropuestas(null);
    try {
      const r = await generarPlan({ dias, desde });
      setPropuestas(r.propuestas || []);
      if (!r.propuestas?.length) flash('La IA no encontró nada que proponer.');
    } catch (e) {
      flash(e?.response?.data?.error || friendlyError(e));
    } finally {
      setGenerando(false);
    }
  }

  async function aceptarTodas() {
    try {
      await crearItems(propuestas.map((p) => ({ ...p, origen: 'ia' })));
      setPropuestas(null);
      flash('Agenda actualizada');
      cargar();
    } catch (e) { flash(friendlyError(e)); }
  }

  async function aceptarUna(i) {
    try {
      await crearItems([{ ...propuestas[i], origen: 'ia' }]);
      setPropuestas((p) => p.filter((_, j) => j !== i));
      cargar();
    } catch (e) { flash(friendlyError(e)); }
  }

  async function onAgregar() {
    if (!form.titulo.trim()) return flash('Poné un título');
    try {
      await crearItems([{ ...form, titulo: form.titulo.trim() }]);
      setForm({ ...EMPTY, fecha: form.fecha });
      cargar();
    } catch (e) { flash(friendlyError(e)); }
  }

  async function alternarHecho(it) {
    try {
      // Las acciones de una crisis no son items de la agenda: se ven acá pero
      // viven en la Sala de Situación. El tilde va contra ese registro, así el
      // progreso de la crisis y la agenda no pueden quedar diciendo cosas
      // distintas.
      if (it.accion) {
        await actualizarAccionCrisis(it.accion.crisis_id, it.accion.accion_id, {
          estado: it.estado === 'hecha' ? 'pendiente' : 'hecha',
        });
      } else {
        await actualizarItem(it.id, { estado: it.estado === 'hecho' ? 'pendiente' : 'hecho' });
      }
      cargar();
    } catch (e) { flash(friendlyError(e)); }
  }

  async function eliminar(it) {
    try { await borrarItem(it.id); cargar(); }
    catch (e) { flash(friendlyError(e)); }
  }

  if (estado.loading) return <TechShell><Loading>Armando la agenda…</Loading></TechShell>;

  return (
    <TechShell>
      <TechPageHeader
        title="Agenda"
        dossier="DÍA A DÍA"
        subtitle="Lo que tenés por delante, armado con los datos de la plataforma: lo que cargás vos, lo que vence en otras secciones y lo que la IA propone."
        actions={(
          <>
            <TechSelect
              value={dias}
              onChange={(e) => setDias(Number(e.target.value))}
              style={{ width: 130 }}
            >
              <option value={3}>3 días</option>
              <option value={7}>7 días</option>
              <option value={14}>14 días</option>
            </TechSelect>
            <TechButton type="button" onClick={onGenerar} disabled={generando}>
              {generando ? <FaSpinner /> : <FaMagic />} {generando ? 'Pensando…' : 'Que la IA arme el plan'}
            </TechButton>
          </>
        )}
      >
        {msg && <StatusPill>{msg}</StatusPill>}
      </TechPageHeader>

      {estado.error && <ErrorBox><FaExclamationTriangle /> {estado.error}</ErrorBox>}

      {/* Propuestas de la IA — se aceptan o se descartan, no se guardan solas. */}
      {propuestas?.length > 0 && (
        <TechSection
          title={`La IA propone ${propuestas.length} acción(es)`}
          icon={<FaMagic />}
          action={(
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              <TechButton type="button" onClick={aceptarTodas}><FaCheck /> Aceptar todo</TechButton>
              <TechButtonGhost type="button" onClick={() => setPropuestas(null)}>
                <FaTimes /> Descartar
              </TechButtonGhost>
            </div>
          )}
        >
          {propuestas.map((p, i) => (
            <Propuesta key={i}>
              <div className="top">
                {nombreDia(p.fecha)} {p.hora ? `· ${p.hora}` : ''} · {p.tipo} · prioridad {p.prioridad}
              </div>
              <div className="titulo">{p.titulo}</div>
              {p.descripcion && <div className="desc">{p.descripcion}</div>}
              {p.motivo && <div className="motivo">Por qué: {p.motivo}</div>}
              <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.55rem' }}>
                <TechButtonGhost type="button" onClick={() => aceptarUna(i)}>
                  <FaPlus /> Sumar a la agenda
                </TechButtonGhost>
                <TechButtonGhost
                  type="button"
                  onClick={() => setPropuestas((v) => v.filter((_, j) => j !== i))}
                >
                  <FaTimes /> No
                </TechButtonGhost>
              </div>
            </Propuesta>
          ))}
        </TechSection>
      )}

      <TechSection title="Cargar algo a mano" icon={<FaPlus />}>
        <Form>
          <div>
            <Lbl>Fecha</Lbl>
            <TechInput
              type="date"
              value={form.fecha}
              onChange={(e) => setForm({ ...form, fecha: e.target.value })}
            />
          </div>
          <div>
            <Lbl>Hora</Lbl>
            <TechInput
              type="time"
              value={form.hora}
              onChange={(e) => setForm({ ...form, hora: e.target.value })}
            />
          </div>
          <div>
            <Lbl>Qué</Lbl>
            <TechInput
              placeholder="Ej: recorrida por barrio Güemes"
              value={form.titulo}
              onChange={(e) => setForm({ ...form, titulo: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') onAgregar(); }}
            />
          </div>
          <div>
            <Lbl>Tipo</Lbl>
            <TechSelect value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>
              <option value="tarea">Tarea</option>
              <option value="reunion">Reunión</option>
              <option value="acto">Acto</option>
              <option value="prensa">Prensa</option>
              <option value="territorio">Territorio</option>
              <option value="concejo">Concejo</option>
            </TechSelect>
          </div>
          <TechButton type="button" onClick={onAgregar}><FaPlus /> Agregar</TechButton>
        </Form>
      </TechSection>

      <TechSection title={`Próximos ${dias} días`} icon={<FaCalendarAlt />}>
        {fechas.map((f) => {
          const items = porDia[f] || [];
          const esHoy = f === hoyISO();
          return (
            <Dia key={f}>
              <div className="cabecera">
                <h3>{nombreDia(f)}</h3>
                {esHoy && <span className="tag">Hoy</span>}
                {items.length === 0 && <span className="vacio">— sin nada agendado</span>}
              </div>
              {items.map((it) => {
                const hecho = it.estado === 'hecho' || it.estado === 'hecha';
                const derivado = it.origen === 'derivado';
                const esCrisis = !!it.accion;
                return (
                  <Item key={it.id} $prio={it.prioridad} $hecho={hecho}>
                    <span className="hora">{it.hora || '—'}</span>
                    <div className="cuerpo">
                      <div className="titulo">{it.titulo}</div>
                      {it.descripcion && <div className="desc">{it.descripcion}</div>}
                      {it.motivo && <div className="motivo">{it.motivo}</div>}
                      <div className="meta">
                        <TechChip tone={esCrisis ? 'danger' : undefined}>{it.tipo}</TechChip>
                        {it.origen === 'ia' && <TechChip>propuesto por IA</TechChip>}
                        {it.estado === 'en_curso' && <TechChip tone="warn">en curso</TechChip>}
                        {derivado && it.seccion && (
                          <Link to={it.seccion} style={{ fontSize: '0.76rem', color: 'var(--btn-primary)' }}>
                            {esCrisis ? 'ver la crisis' : 'ver en su sección'}{' '}
                            <FaArrowRight style={{ fontSize: '0.62rem' }} />
                          </Link>
                        )}
                      </div>
                    </div>
                    {/* Los derivados se resuelven en su sección, no acá: marcarlos
                        "hecho" en la agenda daría una sensación falsa de avance.
                        Las acciones de crisis son la excepción: el tilde escribe
                        sobre crisis_actions, que es su registro real, así que el
                        avance es genuino y no hay dos verdades. */}
                    {(!derivado || esCrisis) && (
                      <div className="acciones">
                        <MiniBtn
                          type="button"
                          onClick={() => alternarHecho(it)}
                          title={hecho ? 'Marcar pendiente' : 'Marcar hecho'}
                        >
                          <FaCheck />
                        </MiniBtn>
                        {/* Borrar NO se ofrece para las de crisis: su id es
                            "cri-<n>", que no es un id de agenda_items — borrarlo
                            desde acá tocaría la fila equivocada. Se dan de baja
                            en la Sala de Situación. */}
                        {!esCrisis && (
                          <MiniBtn type="button" $danger onClick={() => eliminar(it)} title="Eliminar">
                            <FaTrashAlt />
                          </MiniBtn>
                        )}
                      </div>
                    )}
                  </Item>
                );
              })}
            </Dia>
          );
        })}
      </TechSection>

      {sinFecha.length > 0 && (
        <TechSection
          title={`Esperando gestión (${sinFecha.length})`}
          icon={<FaExclamationTriangle />}
          action={<TechChip>sin fecha asignada</TechChip>}
        >
          <Pendientes>
            {sinFecha.map((it) => (
              <Item key={it.id} $prio={it.prioridad} style={{ marginBottom: 0 }}>
                <div className="cuerpo">
                  <div className="titulo">{it.titulo}</div>
                  {it.motivo && <div className="motivo">{it.motivo}</div>}
                  <div className="meta">
                    {it.accion && <TechChip tone="danger">crisis</TechChip>}
                    {it.estado === 'en_curso' && <TechChip tone="warn">en curso</TechChip>}
                    {it.seccion && (
                      <Link to={it.seccion} style={{ fontSize: '0.76rem', color: 'var(--btn-primary)' }}>
                        gestionarlo <FaArrowRight style={{ fontSize: '0.62rem' }} />
                      </Link>
                    )}
                  </div>
                </div>
                {/* Las de crisis se pueden tildar acá porque el tilde va contra
                    crisis_actions; el resto se resuelve en su sección. */}
                {it.accion && (
                  <div className="acciones">
                    <MiniBtn type="button" onClick={() => alternarHecho(it)} title="Marcar hecho">
                      <FaCheck />
                    </MiniBtn>
                  </div>
                )}
              </Item>
            ))}
          </Pendientes>
        </TechSection>
      )}

      {Object.keys(porDia).length === 0 && sinFecha.length === 0 && (
        <Empty>
          No hay nada cargado todavía. Tocá "Que la IA arme el plan" y te propone
          los próximos días con lo que está pendiente en la plataforma.
        </Empty>
      )}
    </TechShell>
  );
}
